import { AdditionalPolicyInfo } from './utils';
import {
  AntimalwareExclusionsListResponse,
  AntimalwareVendorsListResponse,
  ContentControlTrafficExclusion,
  ContentControlTrafficExclusionType,
  FirewallAdvancedProtectionLevel,
  FirewallExclusionItem,
  Inheritance,
  LocalSandbox,
  ModuleId,
  Policy,
  PolicyCounters,
  SectionId,
  TreePath,
} from '@nirvana/shared/dtos/types/policies/common';
import { store } from '@nirvana/redux/store';
import {
  clearSectionMask,
  getModules,
  getSectionValidity,
  reset,
  setInitialTreeState,
  setPolicyDataLoaded,
  setSectionMask,
} from './redux/actions';
import _ from 'lodash';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import deepmerge from 'deepmerge';
import {
  PatchManagementAvailableMaintenanceWindow,
  PatchManagementSelectedMaintenanceWindow,
} from './utils/uiSettings/patchManagement/patch-management.types';
import {
  DataLoadedStatus,
  ModuleState,
  PolicyAppStateModules,
  SectionMask,
  SectionState,
  SectionStatusType,
} from './redux/types';
import { getDefaultModulesState, selectPoliciesState } from './redux/slice';
import { setAvailableMaintenanceWindowsLoadedForPatch } from './redux/customStates/patchManagement/patch-management.actions';
import { RuleSetItem } from '../../integrityMonitor/realTime/integrityMonitorPolicyRuleSetsSection/types';
import {
  setCompanyRuleSetsLoadedForIntegrityMonitor,
  setSelectedRuleSetsLoadedForIntegrityMonitor,
} from './redux/customStates/integrityMonitor/integrity-monitor.actions';
import { setNavigationHeaderSlice, setPolicyName } from './redux/navigationHeaderSlice/actions.navigation';
import { PolicyContext } from './redux/navigationHeaderSlice/types.navigation';
import { ScheduleListItem } from './utils/uiSettings/contentControl/content-control.types';
import { setAvailableSchedulersLoadedForContentControlWeb } from './redux/customStates/contentControl/content-control.actions';
import { PolicyDependenciesContainer } from '../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { PolicySaveRestriction } from './redux/saveButtonSlice/types.save';
import { setSaveRestriction } from './redux/saveButtonSlice/actions.save';
import {
  setExclusionsListsLoadedForAntimalwareSettings,
  setVendorsExclusionsListsLoadedForAntimalwareSettings,
} from './redux/customStates/antimalware/antimalware.actions';
import { AntimalwareVendorsExclusionsUiType } from './utils/uiSettings/antimalware/antimalware.types';
import { Feature } from '@nirvana/shared/constants';
import { validateDuplicates } from './uiSettings/contentControl/traffic/utils/validators';
import { getSectionStatusFromPolicy } from './utils/status';
import { getSectionStatus, setPathToBeUpdatedWithStatus } from './redux/statusSlice/actions.status';
import { SectionsEnabledByFeature, SectionsEnabledBySubscription } from './utils/availability';
import { AVAILABLE_RIGHTS } from '../../../accounts/common/accountRoleAndPrivileges/type';
import { setActiveView, setSearchString } from '@nirvana/redux/searchable-ui-accordions';
import {
  setValidationLoadedForGeneralDetails,
  setValidationLoadedForGeneralSecurityTelemetry,
} from './redux/customStates/general/general.actions';

export class PolicyPanelManager {
  /**
   * Policy manager private instance
   */
  private static instance: PolicyPanelManager;

  /**
   * Current edited/added policy
   */
  private policy: Policy | null;

  /**
   * Initial policy
   */
  private initialPolicy: Policy | null;

  /**
   * Additional information for current edited/added policy
   */
  private additionalPolicyInfo: AdditionalPolicyInfo = {};

  private constructor() {}

  static getInstance(): PolicyPanelManager {
    if (!PolicyPanelManager.instance) {
      PolicyPanelManager.instance = new PolicyPanelManager();
    }
    return PolicyPanelManager.instance;
  }

  /**
   * Reset policy information.
   */
  public reset() {
    this.policy = null;
    this.additionalPolicyInfo = {};
    store.dispatch(reset());
    store.dispatch(setActiveView(''));
    store.dispatch(setSearchString(''));
  }

  /**
   * Set policy name for navigation.
   * @param policyName
   */
  public setPolicyNameForNavigation(policyName: string) {
    store.dispatch(setPolicyName(policyName));
  }

  /**
   * Set policy name and context for navigation.
   * @param policyName
   * @param context
   */
  public setPolicyInfoForNavigation(policyName: string, context: PolicyContext) {
    store.dispatch(setNavigationHeaderSlice({ policyName: policyName, context: context }));
  }

  /**
   * ExtJs useful method to set initial tree state.
   * It will be replaced by getPolicyPanelTreeItems method.
   * @param isPatchManagementModuleAvailable
   * @param isIntegrityMonitorModuleAvailable
   * @param isEdrSensorModuleAvailable
   * @param isEncryptionModuleAvailable
   * @param isStorageProtectionModuleAvailable
   * @param isIndicatorsOfRiskModuleAvailable
   * @param isRelayModuleAvailable
   * @param isLiveSearchModuleAvailable
   * @param isSandboxEndpointSensorSectionAvailable
   * @param isSandboxNetworkSensorSectionAvailable
   * @param isSandboxIcapSensorSectionAvailable
   * @param isSandboxManagerSectionAvailable
   * @param isDeviceControlModuleAvailable
   * @param isApplicationControlModuleAvailable
   * @param isNsxModuleAvailable
   * @param isFirewallModuleAvailable
   * @param isContentControlModuleAvailable
   * @param isContentControlNetworkSectionAvailable
   * @param isContentControlWebSectionAvailable
   * @param isExchangeModuleAvailable
   * @param isBlocklistRulesModuleAvailable
   * @param isAntimalwareModuleAvailable
   * @param isAntimalwareDynamicThreatDefenseSectionAvailable
   * @param isAntimalwareAntiExploitAvailable
   * @param isAntimalwareSecurityServersAvailable
   * @param isGeneralModuleAvailable
   * @param isGeneralSecurityTelemetrySectionAvailable
   * @param phantom
   */
  public setInitialTreeState(
    isPatchManagementModuleAvailable: boolean,
    isIntegrityMonitorModuleAvailable: boolean,
    isEdrSensorModuleAvailable: boolean,
    isEncryptionModuleAvailable: boolean,
    isStorageProtectionModuleAvailable: boolean,
    isIndicatorsOfRiskModuleAvailable: boolean,
    isRelayModuleAvailable: boolean,
    isLiveSearchModuleAvailable: boolean,
    isSandboxEndpointSensorSectionAvailable: boolean,
    isSandboxNetworkSensorSectionAvailable: boolean,
    isSandboxIcapSensorSectionAvailable: boolean,
    isSandboxManagerSectionAvailable: boolean,
    isDeviceControlModuleAvailable: boolean,
    isApplicationControlModuleAvailable: boolean,
    isNsxModuleAvailable: boolean,
    isFirewallModuleAvailable: boolean,
    isContentControlModuleAvailable: boolean,
    isContentControlNetworkSectionAvailable: boolean,
    isContentControlWebSectionAvailable: boolean,
    isExchangeModuleAvailable: boolean,
    isBlocklistRulesModuleAvailable: boolean,
    isAntimalwareModuleAvailable: boolean,
    isAntimalwareDynamicThreatDefenseSectionAvailable: boolean,
    isAntimalwareAntiExploitAvailable: boolean,
    isAntimalwareSecurityServersAvailable: boolean,
    isGeneralModuleAvailable: boolean,
    isGeneralSecurityTelemetrySectionAvailable: boolean,
    phantom: boolean
  ) {
    const defaultModulesState = getDefaultModulesState();
    const initialTreeState: PolicyAppStateModules = {};

    if (isPatchManagementModuleAvailable) {
      initialTreeState[ModuleId.PATCH_MANAGEMENT] = defaultModulesState[ModuleId.PATCH_MANAGEMENT];
    }

    if (isIntegrityMonitorModuleAvailable) {
      initialTreeState[ModuleId.INTEGRITY_MONITOR] = defaultModulesState[ModuleId.INTEGRITY_MONITOR];
    }

    if (isEdrSensorModuleAvailable) {
      initialTreeState[ModuleId.EDR_SENSOR] = defaultModulesState[ModuleId.EDR_SENSOR];
    }

    if (isEncryptionModuleAvailable) {
      initialTreeState[ModuleId.ENCRYPTION] = defaultModulesState[ModuleId.ENCRYPTION];
    }

    if (isStorageProtectionModuleAvailable) {
      initialTreeState[ModuleId.STORAGE_PROTECTION] = defaultModulesState[ModuleId.STORAGE_PROTECTION];
    }

    if (isIndicatorsOfRiskModuleAvailable) {
      initialTreeState[ModuleId.INDICATORS_OF_RISK] = defaultModulesState[ModuleId.INDICATORS_OF_RISK];
    }

    if (isBlocklistRulesModuleAvailable) {
      initialTreeState[ModuleId.BLOCKLIST_RULES] = defaultModulesState[ModuleId.BLOCKLIST_RULES];
    }

    if (isDeviceControlModuleAvailable) {
      initialTreeState[ModuleId.DEVICE_CONTROL] = defaultModulesState[ModuleId.DEVICE_CONTROL];
    }

    if (isRelayModuleAvailable) {
      initialTreeState[ModuleId.RELAY] = defaultModulesState[ModuleId.RELAY];
    }

    if (isLiveSearchModuleAvailable) {
      initialTreeState[ModuleId.LIVE_SEARCH] = defaultModulesState[ModuleId.LIVE_SEARCH];
    }

    if (isNsxModuleAvailable) {
      initialTreeState[ModuleId.NSX] = defaultModulesState[ModuleId.NSX];
    }

    // set sandbox module if at least one of the sections is available
    const isSandboxModuleAvailable =
      isSandboxEndpointSensorSectionAvailable ||
      isSandboxNetworkSensorSectionAvailable ||
      isSandboxIcapSensorSectionAvailable ||
      isSandboxManagerSectionAvailable;
    if (isSandboxModuleAvailable) {
      initialTreeState[ModuleId.SANDBOX] = defaultModulesState[ModuleId.SANDBOX];

      if (!isSandboxEndpointSensorSectionAvailable) {
        _.unset(initialTreeState[ModuleId.SANDBOX]?.sections, [SectionId.ENDPOINT_SENSOR]);
      }

      if (!isSandboxNetworkSensorSectionAvailable) {
        _.unset(initialTreeState[ModuleId.SANDBOX]?.sections, [SectionId.NETWORK_SENSOR]);
      }

      if (!isSandboxIcapSensorSectionAvailable) {
        _.unset(initialTreeState[ModuleId.SANDBOX]?.sections, [SectionId.ICAP_SENSOR]);
      }

      if (!isSandboxManagerSectionAvailable) {
        _.unset(initialTreeState[ModuleId.SANDBOX]?.sections, [SectionId.SANDBOX_MANAGER]);
      }
    }

    if (isApplicationControlModuleAvailable) {
      initialTreeState[ModuleId.APPLICATION_CONTROL] = defaultModulesState[ModuleId.APPLICATION_CONTROL];
    }

    if (isFirewallModuleAvailable) {
      initialTreeState[ModuleId.FIREWALL] = defaultModulesState[ModuleId.FIREWALL];
    }

    if (isContentControlModuleAvailable) {
      initialTreeState[ModuleId.CONTENT_CONTROL] = defaultModulesState[ModuleId.CONTENT_CONTROL];

      if (!isContentControlNetworkSectionAvailable) {
        _.unset(initialTreeState[ModuleId.CONTENT_CONTROL]?.sections, [SectionId.CONTENT_CONTROL_NETWORK_MONITOR]);
      }

      if (!isContentControlWebSectionAvailable) {
        _.unset(initialTreeState[ModuleId.CONTENT_CONTROL]?.sections, [SectionId.CONTENT_CONTROL_WEB]);
      }
    }

    if (isExchangeModuleAvailable) {
      initialTreeState[ModuleId.EXCHANGE_PROTECTION] = defaultModulesState[ModuleId.EXCHANGE_PROTECTION];
    }

    if (isAntimalwareModuleAvailable) {
      initialTreeState[ModuleId.ANTIMALWARE] = defaultModulesState[ModuleId.ANTIMALWARE];

      if (!isAntimalwareDynamicThreatDefenseSectionAvailable) {
        _.unset(initialTreeState[ModuleId.ANTIMALWARE]?.sections, [SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE]);
      }

      if (!isAntimalwareAntiExploitAvailable) {
        _.unset(initialTreeState[ModuleId.ANTIMALWARE]?.sections, [SectionId.ANTIMALWARE_ANTI_EXPLOIT]);
      }

      if (!isAntimalwareSecurityServersAvailable) {
        _.unset(initialTreeState[ModuleId.ANTIMALWARE]?.sections, [SectionId.ANTIMALWARE_SECURITY_SERVERS]);
      }
    }

    initialTreeState[ModuleId.POLICY_SETTINGS] = defaultModulesState[ModuleId.POLICY_SETTINGS];

    if (isGeneralModuleAvailable) {
      initialTreeState[ModuleId.GENERAL] = defaultModulesState[ModuleId.GENERAL];

      if (!isGeneralSecurityTelemetrySectionAvailable) {
        _.unset(initialTreeState[ModuleId.GENERAL]?.sections, [SectionId.SECURITY_TELEMETRY]);
      }
    }
    store.dispatch(setInitialTreeState({ modules: initialTreeState, phantom: phantom }));
  }

  /**
   * Set received policy and trigger modules data loaded.
   */
  public setPolicy(policy: Policy) {
    this.policy = policy;
    this.initialPolicy = _.cloneDeep(policy);

    // save initial values of policy
    this.setAdditionalPolicyInfoToPath(['valueHistory'], {
      antimalware: {
        vendorsExclusionsType: this.policy.uiSettings.antimalware.settings.activateExclusions
          .selectedVendorExclusionLists.length
          ? AntimalwareVendorsExclusionsUiType.CUSTOM
          : AntimalwareVendorsExclusionsUiType.ALL,
      },
      firewall: {
        protectionLevel: this.policy.uiSettings.firewall.advanced.protectionLevel,
      },
      nsx: { enabled: this.policy.uiSettings.nsx?.enabled },
    });

    // save general notifications settings only if silent mode is disabled
    if (!this.policy.uiSettings.general.display.silentMode.enable) {
      this.setAdditionalPolicyInfoToPath(['valueHistory', 'general'], {
        iconNotificationArea: this.policy.uiSettings.general.display.silentMode.iconNotificationArea,
        displayAlertsPopups: this.policy.uiSettings.general.display.silentMode.displayAlertsPopups,
        showPopups: this.policy.uiSettings.general.display.silentMode.showPopups.enable,
        issuesVisibility: this.policy.uiSettings.general.display.silentMode.issuesVisibility.enable,
        endpointRestartPopup: this.policy.uiSettings.general.display.silentMode.endpointRestartPopup.enable,
      });
    }

    this.setAdditionalPolicyInfoToPath(['remoteValidationHistory'], {
      general: {
        isPolicyValid: null,
        value: null,
      },
    });

    store.dispatch(setPolicyDataLoaded(true));
  }

  /**
   * Merge partial policy with existing full policy.
   * @param partialPolicy
   */
  public setPartialPolicy(partialPolicy: FormGroupValue) {
    if (!this.policy) {
      this.policy = partialPolicy as unknown as Policy;
      return;
    }

    const overwriteMerge = (destinationArray, sourceArray, options) => sourceArray;
    this.policy = deepmerge(this.policy, partialPolicy, { arrayMerge: overwriteMerge });
  }

  /**
   * Get policy id.
   */
  public getPolicyId(): string {
    if (!this.policy) {
      return '';
    }

    return this.policy._id;
  }

  /**
   * Get policy for saving.
   */
  public getPolicyForSaving(): Policy | null {
    return this.policy;
  }

  /**
   * Get values from path.
   * @param path
   * @param defaultValue
   */
  public getPolicy(path: string, defaultValue: any = {}) {
    return _.cloneDeep(_.get(this.policy, path, defaultValue));
  }

  /**
   * Set additional policy information to path (used for keeping user changes).
   * @param path
   * @param partialAdditionalInfo
   */
  public setAdditionalPolicyInfoToPath(path: Array<string>, partialAdditionalInfo: any) {
    _.set(this.additionalPolicyInfo, path, partialAdditionalInfo);
  }

  /**
   * Get additional policy information from path.
   * @param path
   * @param defaultValue
   */
  public getAdditionalPolicyInfo(path: Array<string>, defaultValue: any = {}) {
    return _.cloneDeep(_.get(this.additionalPolicyInfo, path, defaultValue));
  }

  /**
   * Update section status based on form value.
   * @param path
   * @param formValue
   */
  public updateSectionStatus(path: TreePath, formValue: FormGroupValue | null) {
    // do not update status if form is not valid
    const isValid = getSectionValidity(store.getState().policiesApp, path);
    if (!isValid) {
      return;
    }

    try {
      const newStatus = getSectionStatusFromPolicy(path, formValue);
      const oldStatus = getSectionStatus(store.getState().policiesApp, path);

      // dispatch only when different
      if (newStatus === oldStatus) {
        return;
      }

      store.dispatch(
        setPathToBeUpdatedWithStatus({
          path: path,
          status: newStatus,
        })
      );
    } catch (e) {
      store.dispatch(
        setPathToBeUpdatedWithStatus({
          path: path,
          status: { type: SectionStatusType.NONE },
        })
      );
    }
  }

  /**
   * Set counters.
   * @param counters
   */
  public setCounters(counters?: PolicyCounters) {
    this.additionalPolicyInfo.counters = counters;
  }

  /**
   * Get policy name of inherited path.
   * @param path
   */
  public getInheritancePolicyNameForPath(path: TreePath): string {
    return (
      this.policy?.inheritance?.find(
        inheritance =>
          inheritance.moduleId === path.moduleId &&
          (inheritance.sectionId === path.sectionId ||
            inheritance.sectionId === SectionId.ALL ||
            inheritance.sectionId === null)
      )?.policyName || 'N/A'
    );
  }

  /**
   * Set inheritance mask.
   * @param inheritance
   */
  public setInheritanceMask(inheritance: Inheritance[]) {
    const config: { path: TreePath; mask: SectionMask }[] = [];
    const addToConfig = (moduleId: ModuleId, sectionId: SectionId) => {
      config.push({
        path: { moduleId: moduleId, sectionId: sectionId },
        mask: SectionMask.INHERITANCE,
      });
    };

    inheritance.forEach(inheritanceItem => {
      addToConfig(inheritanceItem.moduleId, inheritanceItem.sectionId || SectionId.NONE);
    });

    store.dispatch(setSectionMask(config));
  }

  /**
   * Set license restriction mask by feature.
   */
  public applyFeatureRestrictions() {
    const state = selectPoliciesState(store.getState());
    const modules: PolicyAppStateModules = getModules(state);
    const config: { path: TreePath; mask: SectionMask }[] = [];

    // iterate through configured features
    for (const feature of Object.keys(SectionsEnabledByFeature)) {
      if (!Application.Main.hasFeatures([parseInt(feature, 10)])) {
        continue;
      }

      // iterate through all modules
      Object.entries(modules).forEach(([moduleId, moduleState]: [ModuleId, ModuleState]) => {
        const sections = moduleState.sections;

        // iterate through all sections of the module
        Object.entries(sections).forEach(([sectionId, sectionState]: [SectionId, SectionState]) => {
          // check if the section is enabled by the feature
          const isEnabled = SectionsEnabledByFeature[parseInt(feature, 10)].some(
            (path: TreePath) => path.moduleId === moduleId && path.sectionId === sectionId
          );

          // if the section is not enabled by the feature, add it to the mask
          if (!isEnabled) {
            config.push({
              path: { moduleId: moduleId, sectionId: sectionId },
              mask: SectionMask.LICENSE_RESTRICTION,
            });
          }
        });
      });
    }

    if (
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SECURITY_TELEMETRY_USABLE]) &&
      !Application.Main.company.isPartner()
    ) {
      config.push({
        path: { moduleId: ModuleId.GENERAL, sectionId: SectionId.SECURITY_TELEMETRY },
        mask: SectionMask.LICENSE_RESTRICTION,
      });
    }

    store.dispatch(setSectionMask(config));
  }

  /**
   * Apply default policy restrictions.
   */
  public applyDefaultPolicyRestrictions() {
    const state = selectPoliciesState(store.getState());
    const modules: PolicyAppStateModules = getModules(state);
    const config: { path: TreePath; mask: SectionMask }[] = [];

    // iterate through all modules
    Object.entries(modules).forEach(([moduleId, moduleState]: [ModuleId, ModuleState]) => {
      const sections = moduleState.sections;

      // iterate through all sections of the module
      Object.entries(sections).forEach(([sectionId, sectionState]: [SectionId, SectionState]) => {
        config.push({
          path: { moduleId: moduleId, sectionId: sectionId },
          mask: SectionMask.DEFAULT,
        });
      });
    });

    store.dispatch(setSectionMask(config));
  }

  /**
   * Apply editing access restrictions.
   * @param phantom
   */
  public applyEditingAccessRestrictions(phantom: boolean) {
    // check for editing access restrictions
    if (!phantom) {
      // check default
      if (!!this.policy?.default) {
        store.dispatch(setSaveRestriction(PolicySaveRestriction.DEFAULT_POLICY));
        this.applyDefaultPolicyRestrictions();
        return;
      }

      // check readonly
      if (!!this.policy?.readOnly) {
        if (this.policy.company?.id !== Application.Main.user.company.id) {
          store.dispatch(setSaveRestriction(PolicySaveRestriction.OTHER_COMPANY));
        } else {
          store.dispatch(setSaveRestriction(PolicySaveRestriction.OTHER_USER));
        }
        return;
      }
    }

    // check for rights
    if (!Application.Main.hasAnyRights([Constants.Rights.RIGHT_MANAGE_POLICIES_WRITE])) {
      store.dispatch(setSaveRestriction(PolicySaveRestriction.NO_WRITE_RIGHTS));
    }
  }

  /**
   * Set license restriction mask by subscription.
   */
  public applySubscriptionRestrictions() {
    if (Application.Main.product.isGZ() || Application.Main.canHaveChildCompanies()) {
      return;
    }

    // check subscription
    const subscription = PolicyDependenciesContainer.getInstance()
      .resolveDependency('Company')
      ?.getCompanySubscription();
    if (!subscription || !Object.keys(SectionsEnabledBySubscription).includes(subscription.toString())) {
      return;
    }

    const state = selectPoliciesState(store.getState());
    const modules: PolicyAppStateModules = getModules(state);
    const config: { path: TreePath; mask: SectionMask }[] = [];

    // iterate through all modules
    Object.entries(modules).forEach(([moduleId, moduleState]: [ModuleId, ModuleState]) => {
      const sections = moduleState.sections;

      // iterate through all sections of the module
      Object.entries(sections).forEach(([sectionId, sectionState]: [SectionId, SectionState]) => {
        // check if the section is enabled by the feature
        const isEnabled = SectionsEnabledBySubscription[subscription].some(
          (path: TreePath) => path.moduleId === moduleId && path.sectionId === sectionId
        );

        // if the section is not enabled by the feature, add it to the mask
        if (!isEnabled) {
          config.push({
            path: { moduleId: moduleId, sectionId: sectionId },
            mask: SectionMask.LICENSE_RESTRICTION,
          });
        }
      });
    });

    store.dispatch(setSectionMask(config));
  }

  /* region Patch Management */
  /**
   * Set additional policy information for patch management: available maintenance windows.
   * @param availableMaintenanceWindows
   */
  public setPatchManagementAvailableMaintenanceWindowsPolicyInfo(
    availableMaintenanceWindows: Array<PatchManagementAvailableMaintenanceWindow>
  ) {
    if (!this.additionalPolicyInfo.patchManagement) {
      this.additionalPolicyInfo.patchManagement = {};
    }

    this.additionalPolicyInfo.patchManagement.availableMaintenanceWindows = availableMaintenanceWindows;
    store.dispatch(setAvailableMaintenanceWindowsLoadedForPatch(DataLoadedStatus.SUCCESS));
  }
  public setPatchManagementAvailableMaintenanceWindowsErrorState() {
    store.dispatch(setAvailableMaintenanceWindowsLoadedForPatch(DataLoadedStatus.ERROR));
  }

  public setGeneralDetailsValidation(value: string, policyValidity?: boolean) {
    this.setAdditionalPolicyInfoToPath(['remoteValidationHistory', 'general', 'details'], {
      isPolicyValid: policyValidity,
      value: value,
    });
    store.dispatch(setValidationLoadedForGeneralDetails(DataLoadedStatus.SUCCESS));
  }

  public setGeneralSecurityTelemetryValidation(value: string, serverUrlValidity?: boolean) {
    this.setAdditionalPolicyInfoToPath(['remoteValidationHistory', 'general', 'securityTelemetry'], {
      serverUrlValidity: serverUrlValidity,
      value: value,
    });
    store.dispatch(setValidationLoadedForGeneralSecurityTelemetry(DataLoadedStatus.SUCCESS));
  }

  /**
   * Cache additional policy information for patch management: selected maintenance windows.
   * @param selectedMaintenanceWindow
   */
  public cachePatchManagementSelectedMaintenanceWindowPolicyInfo(
    selectedMaintenanceWindow: PatchManagementSelectedMaintenanceWindow
  ) {
    if (!this.additionalPolicyInfo.patchManagement) {
      this.additionalPolicyInfo.patchManagement = {};
    }

    if (!this.additionalPolicyInfo.patchManagement.selectedMaintenanceWindows) {
      this.additionalPolicyInfo.patchManagement.selectedMaintenanceWindows = [];
    }

    const selectedMaintenanceWindows = this.additionalPolicyInfo.patchManagement.selectedMaintenanceWindows;
    const index = selectedMaintenanceWindows.findIndex(window => window._id === selectedMaintenanceWindow._id);

    // cache selected window if missing
    if (index === -1) {
      selectedMaintenanceWindows.push(selectedMaintenanceWindow);
    }
  }

  /**
   * Check if maintenance window is cached.
   * @param windowId
   */
  public isMaintenanceWindowCached(windowId: string): boolean {
    if (!this.additionalPolicyInfo.patchManagement?.selectedMaintenanceWindows) {
      return false;
    }

    return this.additionalPolicyInfo.patchManagement.selectedMaintenanceWindows.some(window => window._id === windowId);
  }

  /**
   * Get cached patch management selected maintenance window policy information.
   * @param selectedMaintenanceWindowId
   */
  public getCachedPatchManagementSelectedMaintenanceWindowPolicyInfo(
    selectedMaintenanceWindowId: string
  ): PatchManagementSelectedMaintenanceWindow | undefined {
    if (!this.additionalPolicyInfo.patchManagement) {
      return;
    }

    if (!this.additionalPolicyInfo.patchManagement.selectedMaintenanceWindows) {
      return;
    }

    return this.additionalPolicyInfo.patchManagement.selectedMaintenanceWindows.find(
      window => window._id === selectedMaintenanceWindowId
    );
  }
  /* endregion Patch Management */

  /* region Integrity Monitor */
  public setIntegrityMonitorCompanyRuleSetsPolicyInfo(companyRuleSets: Array<RuleSetItem>) {
    if (!this.additionalPolicyInfo.integrityMonitor) {
      this.additionalPolicyInfo.integrityMonitor = {};
    }

    this.additionalPolicyInfo.integrityMonitor.companyRuleSets = companyRuleSets;
    store.dispatch(setCompanyRuleSetsLoadedForIntegrityMonitor(DataLoadedStatus.SUCCESS));
  }
  public setIntegrityMonitorCompanyRuleSetsErrorState() {
    store.dispatch(setCompanyRuleSetsLoadedForIntegrityMonitor(DataLoadedStatus.ERROR));
  }

  public setIntegrityMonitorRuleSetsPolicyInfo(selectedRuleSets: Array<RuleSetItem>) {
    if (!this.additionalPolicyInfo.integrityMonitor) {
      this.additionalPolicyInfo.integrityMonitor = {};
    }

    this.additionalPolicyInfo.integrityMonitor.selectedRuleSets = selectedRuleSets;
    store.dispatch(setSelectedRuleSetsLoadedForIntegrityMonitor(DataLoadedStatus.SUCCESS));
  }
  public setIntegrityMonitorRuleSetsErrorState() {
    store.dispatch(setSelectedRuleSetsLoadedForIntegrityMonitor(DataLoadedStatus.ERROR));
  }
  /* endregion Integrity Monitor */

  /* region Sandbox */
  public setLocalSandboxesPolicyInfo(localSandboxes: LocalSandbox[]): void {
    if (!this.additionalPolicyInfo.sandbox) {
      this.additionalPolicyInfo.sandbox = {};
    }

    this.additionalPolicyInfo.sandbox.localSandboxes = localSandboxes;
  }

  public getLocalSandboxesPolicyInfo(): LocalSandbox[] | undefined {
    return this.additionalPolicyInfo.sandbox?.localSandboxes;
  }

  public getLocalSandboxPolicyInfoByEndpointId(endpointId: string): LocalSandbox | undefined {
    return this.additionalPolicyInfo.sandbox?.localSandboxes?.find(sandbox => sandbox.endpointId === endpointId);
  }
  /* endregion Sandbox */

  /* region Network protection */
  /**
   * Set masks for sections when network protection is disabled.
   * @param isDisabled
   */
  public setContentControlSectionsMask(isDisabled: boolean): void {
    const sectionIds = [
      SectionId.CONTENT_CONTROL_WEB,
      SectionId.CONTENT_CONTROL_DATA_PROTECTION,
      SectionId.CONTENT_CONTROL_NETWORK_MONITOR,
    ];
    const config = sectionIds.map(sectionId => {
      return {
        path: { moduleId: ModuleId.CONTENT_CONTROL, sectionId: sectionId },
        mask: SectionMask.NETWORK_PROTECTION_TRAFFIC,
      };
    });

    store.dispatch(isDisabled ? setSectionMask(config) : clearSectionMask(config));
  }

  /**
   * Set additional policy information for content control - web: available schedulers.
   * @param response
   */
  public setContentControlWebAvailableSchedulersPolicyInfo(response: { success: boolean; data: ScheduleListItem[] }) {
    if (!this.additionalPolicyInfo.contentControl) {
      this.additionalPolicyInfo.contentControl = {};
    }

    this.additionalPolicyInfo.contentControl.scheduleList = response.data;
    store.dispatch(setAvailableSchedulersLoadedForContentControlWeb(DataLoadedStatus.SUCCESS));
  }
  public setContentControlWebAvailableSchedulersPolicyInfoErrorState() {
    store.dispatch(setAvailableSchedulersLoadedForContentControlWeb(DataLoadedStatus.ERROR));
  }
  /* endregion Network protection */

  /* region General */
  /**
   * Check if silent mode is dependent of firewall protection level value.
   */
  public isSilentModeDependentOfFirewallProtectionLevelValue(): boolean {
    if (!this.policy) {
      return false;
    }

    return (
      !this.policy.uiSettings.general.display.silentMode.enable &&
      this.isFirewallProtectionLevelValueDependentOfGeneralNotifications(
        this.policy.uiSettings.firewall.advanced.protectionLevel
      )
    );
  }

  /**
   * Check if display alert popups is dependent of firewall protection level value.
   */
  public isDisplayAlertPopupsDependentOfFirewallProtectionLevelValue(): boolean {
    if (!this.policy) {
      return false;
    }

    return this.isFirewallProtectionLevelValueDependentOfGeneralNotifications(
      this.policy.uiSettings.firewall.advanced.protectionLevel
    );
  }

  /**
   * Restore firewall protection level value from history.
   */
  public restoreFirewallProtectionLevelFromHistory() {
    if (!this.policy) {
      return;
    }

    // restore old value from history
    const valueHistory = this.getAdditionalPolicyInfo(['valueHistory', 'firewall', 'protectionLevel'], null);
    if (valueHistory !== null) {
      _.set(this.policy, ['uiSettings', 'firewall', 'advanced', 'protectionLevel'], valueHistory);
      this.setAdditionalPolicyInfoToPath(['valueHistory', 'firewall', 'protectionLevel'], null);
    }
  }

  /**
   * Set firewall protection level to default if necessary.
   * If firewall protection level is dependent of general notifications, set it to default value.
   * If policy is not dependent of general notifications, skip.
   */
  public setFirewallProtectionLevelToDefaultIfNecessary() {
    // skip if policy is not dependent of general notifications
    if (
      !this.policy ||
      !this.isFirewallProtectionLevelValueDependentOfGeneralNotifications(
        this.policy.uiSettings.firewall.advanced.protectionLevel
      )
    ) {
      return;
    }

    // save old value to history
    this.setAdditionalPolicyInfoToPath(
      ['valueHistory', 'firewall', 'protectionLevel'],
      this.policy.uiSettings.firewall.advanced.protectionLevel
    );

    _.set(
      this.policy,
      ['uiSettings', 'firewall', 'advanced', 'protectionLevel'],
      FirewallAdvancedProtectionLevel.RULESET_KNOWN_FILES_AND_ALLOW
    );
  }
  /* endregion General */

  /* region Firewall */
  /**
   * Check if firewall protection level value is dependent of general notifications.
   * @param value
   */
  public isFirewallProtectionLevelValueDependentOfGeneralNotifications(
    value: FirewallAdvancedProtectionLevel
  ): boolean {
    const protectionLevelValuesDependentOfSilentMode = [
      FirewallAdvancedProtectionLevel.RULESET_AND_ASK,
      FirewallAdvancedProtectionLevel.RULESET_KNOWN_FILES_AND_ASK,
    ];

    return protectionLevelValuesDependentOfSilentMode.includes(value);
  }

  /**
   * Check if general silent mode is impacted by firewall protection level change.
   *
   * This is used to determine if we need to show an info message in firewall section that silent mode will be unchecked
   * if the protection level is changed to a value that is dependent of general notifications.
   */
  public isGeneralSilentModeImpactedByFirewallProtectionLevelChange() {
    if (!this.policy) {
      return false;
    }

    return this.policy.uiSettings.general.display.silentMode.enable;
  }

  /**
   * Check if general display alerts popups is impacted by firewall protection level change.
   *
   * This is used to determine if we need to show an info message in firewall section
   * that display alerts popups is unchecked
   * and protection level is better to not be changed to a value that is dependent of general notifications.
   */
  public isGeneralDisplayAlertsImpactedByFirewallProtectionLevelChange() {
    if (!this.policy) {
      return false;
    }

    return !this.policy.uiSettings.general.display.silentMode.displayAlertsPopups;
  }

  /**
   * Set general silent mode to false.
   */
  public unsetGeneralDisplaySilentMode() {
    if (!this.policy) {
      return;
    }

    _.set(this.policy, ['uiSettings', 'general', 'display', 'silentMode', 'enable'], false);

    // update status
    this.updateSectionStatus(
      {
        moduleId: ModuleId.GENERAL,
        sectionId: SectionId.GENERAL_NOTIFICATIONS,
      },
      {
        uiSettings: {
          general: {
            display: {
              silentMode: {
                enable: this.policy.uiSettings[ModuleId.GENERAL].display.silentMode.enable,
              },
            },
          },
        },
      }
    );
  }

  /**
   * Duplicate exclusion item to content control. Validate duplicates.
   * @param exclusion
   */
  public duplicateExclusionItemToContentControl(exclusion: FirewallExclusionItem) {
    // compute new item
    const newItem: ContentControlTrafficExclusion = {
      type: ContentControlTrafficExclusionType.IP,
      entity: exclusion.ip,
      remarks: exclusion.description,
    };

    // skip duplicates
    const validationResult = validateDuplicates(
      newItem,
      this.policy?.uiSettings.contentControl.traffic.exclusions || []
    );
    if (!validationResult.valid) {
      return;
    }

    // add new item
    this.policy?.uiSettings.contentControl.traffic.exclusions?.push(newItem);
  }
  /* endregion Firewall */

  /* region Antimalware */
  /**
   * Set masks for sections when antimaleware is disabled.
   * @param isDisabled
   */
  public setAntimalwareSectionsMask(isDisabled: boolean): void {
    const paths = [
      { moduleId: ModuleId.SANDBOX, sectionId: SectionId.ENDPOINT_SENSOR },
      { moduleId: ModuleId.ANTIMALWARE, sectionId: SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE },
      { moduleId: ModuleId.ANTIMALWARE, sectionId: SectionId.ANTIMALWARE_ON_EXECUTE },
    ];
    const config = paths.map(path => {
      return {
        path: path,
        mask: SectionMask.ANTIMALWARE_ON_ACCESS,
      };
    });

    store.dispatch(isDisabled ? setSectionMask(config) : clearSectionMask(config));
  }

  public setAntimalwareExclusionListsPolicyInfo(exclusionsLists: Array<AntimalwareExclusionsListResponse>) {
    if (!this.additionalPolicyInfo.antimalware) {
      this.additionalPolicyInfo.antimalware = {};
    }

    this.additionalPolicyInfo.antimalware.exclusionsLists = exclusionsLists;
    store.dispatch(setExclusionsListsLoadedForAntimalwareSettings(DataLoadedStatus.SUCCESS));
  }
  public setAntimalwareExclusionListsErrorState() {
    store.dispatch(setExclusionsListsLoadedForAntimalwareSettings(DataLoadedStatus.ERROR));
  }

  public setAntimalwareVendorsExclusionListsPolicyInfo(exclusionsLists: Array<AntimalwareVendorsListResponse>) {
    if (!this.additionalPolicyInfo.antimalware) {
      this.additionalPolicyInfo.antimalware = {};
    }

    this.additionalPolicyInfo.antimalware.vendorsExclusionsLists = exclusionsLists;
    store.dispatch(setVendorsExclusionsListsLoadedForAntimalwareSettings(DataLoadedStatus.SUCCESS));
  }
  public setAntimalwareVendorsExclusionListsErrorState() {
    store.dispatch(setVendorsExclusionsListsLoadedForAntimalwareSettings(DataLoadedStatus.ERROR));
  }
  /* endregion Antimalware */

  /* region Exchange */
  /**
   * Remove deleted groups from record.
   * @param guids
   */
  public removeDeletedGroupsFromRecord(guids: string[]) {
    let itemsToUpdate;

    const filterFn = (path: string[]) => {
      if (!this.policy) {
        return;
      }

      itemsToUpdate = _.cloneDeep(_.get(this.policy, path, []));
      itemsToUpdate.forEach(item => {
        if (item.scope.fromSpecific) {
          item.scope.fromSpecific = item.scope.fromSpecific.filter((guid: string) => !guids.includes(guid));
        }

        if (item.scope.toSpecific) {
          item.scope.toSpecific = item.scope.toSpecific.filter((guid: string) => !guids.includes(guid));
        }
      });

      _.set(this.policy, path, itemsToUpdate);
    };

    // exchange antimalware section
    filterFn(['uiSettings', 'exchange', 'antimalware', 'rules']);
    filterFn(['uiSettings', 'exchange', 'antimalware', 'exclusions']);

    // exchange antispam section
    filterFn(['uiSettings', 'exchange', 'antispam', 'rules']);

    // exchange content control section
    filterFn(['uiSettings', 'exchange', 'contentControl', 'contentFiltering', 'rules']);
    filterFn(['uiSettings', 'exchange', 'contentControl', 'attachmentFiltering', 'rules']);
  }

  /* endregion Exchange */

  /* region Visibility */
  public isGeneralNotificationsAvailable(): boolean {
    return !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  }

  public isGeneralSecurityTelemetryAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SECURITY_TELEMETRY_VISIBLE]);
  }

  public isPatchManagementModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isIntegrityMonitorModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_INTEGRITY_MONITOR_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isStorageProtectionModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_STORAGE_PROTECTION_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isIndicatorsOfRiskModuleAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_IOR_VISIBLE]);
  }

  public isEdrSensorModuleAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_ANY_EDR_VISIBLE]);
  }

  public isLiveSearchModuleAvailable() {
    return (
      Application.Main.hasAnyRights([AVAILABLE_RIGHTS.MANAGE_REMOTE_SHELL]) &&
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_LIVE_SEARCH_VISIBLE])
    );
  }

  public isEncryptionModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_VOLUME_ENCRYPTION_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isSandboxEndpointSensorSectionAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_VISIBLE]);
  }

  public isSandboxNetworkSensorSectionAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_GHOSTR_VISIBLE]) && Application.Main.product.isGZ();
  }

  public isSandboxIcapSensorSectionAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_STORAGE_PROTECTION_VISIBLE]) &&
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_LOCAL_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isSandboxManagerSectionAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_LOCAL_VISIBLE]) && Application.Main.product.isGZ()
    );
  }

  public isNsxModuleAvailable() {
    return (
      !(Application.Main.product.isCloud() || !Application.Main.hasNsxIntegrationInUse()) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isApplicationControlModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_APPLICATION_CONTROL]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isDeviceControlModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_DEVICE_CONTROL_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isFirewallModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_FIREWALL_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isContentControlWebSectionAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_CONTENT_CONTROL_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isContentControlNetworkSectionAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_NETWORK_MONITOR_VISIBLE]);
  }

  public isExchangeModuleAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EXCHANGE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isAntimalwareOnAccessSectionAvailable() {
    return !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  }

  public isAntimalwareOnDemandSectionAvailable() {
    return !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  }

  public isAntimalwareAntiTamperingSectionAvailable() {
    return !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  }

  public isAntimalwareDynamicThreatDefenseSectionAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_ADVANCED_THREAT_PREVENTION_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isAntimalwareAntiExploitSectionAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EXPLOIT_MITIGATION_VISIBLE]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isAntimalwareSecurityServersSectionAvailable() {
    return (
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING]) &&
      !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
    );
  }

  public isAntimalwareSettingsSectionAvailable() {
    return !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
  }

  public isBlocklistModuleAvailable() {
    return Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_BLOCKLIST]);
  }
  /* endregion Visibility */

  /* region Network Protection */
  public setNetworkProtectionEmailTrafficScan(path: Array<string>, value: boolean) {
    if (!this.policy) {
      return;
    }

    _.set(
      this.policy,
      ['uiSettings', 'contentControl', 'traffic', 'trafficScan', ...path],
      value
    );
  }
  /* endregion Network Protection */

  /**
   * Track analytics for PHASR module changes when policy is saved.
   * @private
   */
  public trackPhasrAnalyticsOnSave(): void {
    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PHASR_VISIBLE])) {
      return;
    }

    const currentPhasrConfig = this.getPolicy('uiSettings.phasr');
    if (!currentPhasrConfig) {
      return;
    }

    const initialPhasrConfig = _.get(this.initialPolicy, 'uiSettings.phasr');
    if (!initialPhasrConfig) {
      return;
    }

    try {
      const phasrEnabled = currentPhasrConfig.enabled;
      if (phasrEnabled !== initialPhasrConfig.enabled) {
        analytics.trackPageInteraction({
          reference: 'phasr_policy:module',
          asset: phasrEnabled ? 'enabled' : 'disabled',
        });
      }
    } finally {
    }

    const currentCategories = currentPhasrConfig.categories;
    const initialCategories = _.get(initialPhasrConfig, 'categories');
    for (const category of Object.keys(currentCategories)) {
      const currentCategoryValue = currentCategories[category];
      const initialCategoryValue = initialCategories[category];

      const enabled = currentCategoryValue.enabled;
      const autopilot = currentCategoryValue.autopilot;

      try {
        if (enabled !== initialCategoryValue.enabled || autopilot !== initialCategoryValue.autopilot) {
          analytics.trackPageInteraction({
            reference: `phasr_policy:${category}`,
            asset: `${enabled ? (autopilot ? 'autopilot' : 'direct_control') : 'disabled'}`,
          });
        }
      } finally {
      }
    }
  }
}
